import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ACTIONS_COL_WIDTH, Button, ClampText, DataTable, EmptyValue, Alert, Badge, StatusBadge, Spinner, type DataTableColumn } from '@/components/ui';
import {
  workIdService,
  WorkIdRecord,
  WorkIdQueryParams,
  WorkIdFilterOptions,
  WorkIdListFilters
} from '../workIdService';
import ServerPagination from '../../../components/ServerPagination';
import WorkIdFilters, { type WorkIdFiltersApplyPayload } from './WorkIdFilters';
import WorkIdActions from './WorkIdActions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatWorkIdStatus } from '../utils/workIdStatusConstants';
import { taskPageUrl } from '../utils/taskPageUrl';
import { getPriorityVariant, formatPriority } from '../utils/priorityDisplay';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';
import { describeActionError } from '../../../utils/loadError';
import { usePersistedListState } from '../../../hooks/usePersistedListState';

interface WorkIdsTableProps {
  refreshKey: number;
  filters: WorkIdListFilters;
  onFiltersChange: (filters: WorkIdListFilters) => void;
  filterOptions: WorkIdFilterOptions;
  search?: string;
  onSearchCleared?: () => void;
  clearFiltersTrigger?: number;
  onHasActiveFiltersChange?: (value: boolean) => void;
  onApprove?: (record: WorkIdRecord) => void;
  onCancel?: (record: WorkIdRecord) => void;
  onCloseWithoutAction?: (record: WorkIdRecord) => void;
  onReopen?: (record: WorkIdRecord) => void;
  onAssignExisting?: (record: WorkIdRecord) => void;
  onCreateNewTask?: (record: WorkIdRecord) => void;
  onUnassign?: (record: WorkIdRecord) => void;
  onMerge?: (record: WorkIdRecord) => void;
  onViewMergeSuggestions?: (record: WorkIdRecord) => void;
  onUnmerge?: (record: WorkIdRecord) => void;
  onReviewHistory?: (record: WorkIdRecord) => void;
  /**
   * Open details in view mode (row click).
   */
  onOpenDetail?: (record: WorkIdRecord) => void;
  /**
   * Open details specifically for editing (action-bar Edit).
   */
  onEdit?: (record: WorkIdRecord) => void;
  onReopenTask?: (record: WorkIdRecord) => void;
  /** When set, MERGED rows with a known parent show a clickable status badge that opens the parent detail modal. */
  onOpenMergedParent?: (record: WorkIdRecord) => void;
  onReportAccomplishment?: (record: WorkIdRecord) => void;
  onCloseTask?: (record: WorkIdRecord) => void;
  /** When true, filters are rendered by the parent (e.g. above search bar); do not show filters here. */
  hideFilters?: boolean;
}

const WorkIdsTable: React.FC<WorkIdsTableProps> = ({
  refreshKey,
  filters: externalFilters,
  onFiltersChange,
  filterOptions: externalFilterOptions,
  search: externalSearch,
  onSearchCleared,
  clearFiltersTrigger,
  onHasActiveFiltersChange,
  onApprove,
  onCancel,
  onCloseWithoutAction,
  onReopen,
  onAssignExisting,
  onCreateNewTask,
  onUnassign,
  onMerge,
  onViewMergeSuggestions,
  onUnmerge,
  onReviewHistory,
  onOpenDetail,
  onEdit,
  onReopenTask,
  onOpenMergedParent,
  onReportAccomplishment,
  onCloseTask,
  hideFilters = false,
}) => {
  const { hasPermission } = usePermissions();
  const hasWorkIdPerm = (key: string) => hasPermission(`workId:${key}`);
  const canApprove = hasWorkIdPerm('approve');
  const canCancel = hasWorkIdPerm('cancel');
  const canAssign = hasWorkIdPerm('assign');
  const canMerge = hasWorkIdPerm('merge');
  const canCloseWithoutAction = hasWorkIdPerm('close_without_action');
  const canEdit = hasWorkIdPerm('create') || hasWorkIdPerm('approve');
  const canReviewHistory = hasWorkIdPerm('review_history');
  const canExport = hasWorkIdPerm('view');

  const [workIds, setWorkIds] = useState<WorkIdRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // V2-024 / FLT-09: page, page size and sort are kept for the sign-in with the page's filters.
  const [savedPage, setSavedPage] = usePersistedListState('workIds', 'page', { page: 1, limit: 20 });
  const [pagination, setPagination] = useState({
    page: savedPage.page,
    limit: savedPage.limit,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  // Server-side sort (allowlisted server-side). null = server default (createdAt desc).
  const [sort, setSort] = usePersistedListState<{ sortBy: string | null; sortDir: 'asc' | 'desc' }>(
    'workIds',
    'sort',
    { sortBy: null, sortDir: 'desc' }
  );
  const { sortBy, sortDir } = sort;
  useEffect(() => {
    setSavedPage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit, setSavedPage]);
  const filters = externalFilters;
  const filterOptions = externalFilterOptions;
  const fetchAbortRef = useRef<AbortController | null>(null);
  const prevExternalSearchRef = useRef(externalSearch);
  // A different filter set starts again at page 1 (the remembered page belongs to the old one).
  const filtersSignature = JSON.stringify(filters);
  const prevFiltersSignatureRef = useRef(filtersSignature);

  const fetchWorkIds = async (resetPage = false, signal?: AbortSignal) => {
    let aborted = false;
    try {
      setLoading(true);
      setError(null);

      const currentPage = resetPage ? 1 : pagination.page;
      const params: WorkIdQueryParams = {
        page: currentPage,
        limit: pagination.limit,
        ...(filters.status && filters.status !== 'exclude-cancelled' && { status: filters.status }),
        ...(filters.foremanAreaId && { foremanAreaId: filters.foremanAreaId }),
        ...(filters.serviceAreaId && { serviceAreaId: filters.serviceAreaId }),
        ...(filters.assetType && { assetType: filters.assetType }),
        ...((externalSearch ?? filters.search ?? '') && { search: externalSearch ?? filters.search ?? '' }),
        ...(filters.priority && { priority: filters.priority }),
        ...(filters.reportedById && { reportedById: filters.reportedById }),
        ...(filters.workSource && { workSource: filters.workSource }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
        ...((filters.dateFilterType === 'taskCompletion' || filters.dateFilterType === 'reportedOn') && {
          dateFilterType: filters.dateFilterType
        }),
        ...(sortBy && { sortBy, sortOrder: sortDir })
      };

      const response = await workIdService.getWorkIds(params, signal);

      if (signal?.aborted) {
        aborted = true;
        return;
      }

      setWorkIds(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNextPage: response.pagination.hasNextPage,
        hasPreviousPage: response.pagination.hasPreviousPage
      });

      if (resetPage) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        aborted = true;
        return;
      }
      console.error('Error loading work IDs:', err);
      setError('Failed to load work IDs. Please try again later.');
      setWorkIds([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
    } finally {
      if (!aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const externalSearchChanged = prevExternalSearchRef.current !== externalSearch;
    if (externalSearchChanged) prevExternalSearchRef.current = externalSearch;
    const filtersChanged = prevFiltersSignatureRef.current !== filtersSignature;
    if (filtersChanged) prevFiltersSignatureRef.current = filtersSignature;
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    fetchWorkIds(externalSearchChanged || filtersChanged, controller.signal);
    return () => {
      controller.abort();
      fetchAbortRef.current = null;
    };
  }, [
    refreshKey,
    pagination.page,
    pagination.limit,
    filters.status,
    filters.foremanAreaId,
    filters.serviceAreaId,
    filters.assetType,
    filters.search,
    filters.priority,
    filters.reportedById,
    filters.workSource,
    filters.dateFrom,
    filters.dateTo,
    filters.dateFilterType,
    externalSearch,
    sortBy,
    sortDir
  ]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSort({ sortBy: key, sortDir: dir });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination(prev => ({
      ...prev,
      limit: newPageSize,
      page: 1
    }));
  };

  const handleApplyFilters = (newFilters: WorkIdFiltersApplyPayload) => {
    onFiltersChange({
      ...filters,
      ...newFilters,
      search: filters.search ?? ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters =
    filters.serviceAreaId !== '' ||
    filters.foremanAreaId !== '' ||
    filters.assetType !== '' ||
    (filters.status !== '' && filters.status !== 'exclude-cancelled') ||
    (filters.priority !== '' && filters.priority != null) ||
    (filters.dateFrom ?? '') !== '' ||
    (filters.dateTo ?? '') !== '' ||
    (filters.reportedById ?? '') !== '' ||
    (filters.workSource ?? '') !== '' ||
    (externalSearch ?? filters.search) !== '';

  const handleClearFilters = () => {
    onFiltersChange({
      serviceAreaId: '',
      foremanAreaId: '',
      assetType: '',
      status: 'exclude-cancelled',
      search: '',
      priority: '',
      dateFrom: '',
      dateTo: '',
      dateFilterType: 'reportedOn',
      reportedById: '',
      workSource: ''
    });
    onSearchCleared?.();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const prevClearTriggerRef = useRef(0);
  useEffect(() => {
    onHasActiveFiltersChange?.(hasActiveFilters);
  }, [hasActiveFilters, onHasActiveFiltersChange]);

  useEffect(() => {
    if (clearFiltersTrigger != null && onFiltersChange && clearFiltersTrigger > prevClearTriggerRef.current) {
      prevClearTriggerRef.current = clearFiltersTrigger;
      onFiltersChange({
        serviceAreaId: '',
        foremanAreaId: '',
        assetType: '',
        status: 'exclude-cancelled',
        search: '',
        priority: '',
        dateFrom: '',
        dateTo: '',
        dateFilterType: 'reportedOn',
        reportedById: '',
        workSource: ''
      });
      onSearchCleared?.();
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  }, [clearFiltersTrigger, onSearchCleared, onFiltersChange]);

  const formatReportedBy = (record: WorkIdRecord): string => {
    if (!record.reportedBy) return '';
    const { name, lastName } = record.reportedBy;
    return [name, lastName].filter(Boolean).join(' ');
  };

  const formatDate = (dateStr: string): string => {
    return formatPacificDate(dateStr) || dateStr;
  };

  const formatSource = (record: WorkIdRecord): string => {
    if (record.createdFromForm?.name) return record.createdFromForm.name;
    if (record.formName) return record.formName;
    return '-';
  };

  const formatAsset = (record: WorkIdRecord): string => {
    if (!record.asset) return '-';
    const name = record.asset.name || '';
    const num = record.asset.assetNumber;
    return num ? `${name} #${num}` : name || '-';
  };

  const [exportingId, setExportingId] = useState<number | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleOpenDetail = (record: WorkIdRecord) => {
    onOpenDetail?.(record);
  };

  const handleEdit = (record: WorkIdRecord) => {
    onEdit?.(record);
  };

  const handleApprove = (record: WorkIdRecord) => {
    onApprove?.(record);
  };

  const handleCancel = (record: WorkIdRecord) => {
    onCancel?.(record);
  };

  const handleCloseWithoutAction = (record: WorkIdRecord) => {
    onCloseWithoutAction?.(record);
  };

  const handleReopen = (record: WorkIdRecord) => {
    onReopen?.(record);
  };

  const handleMerge = (record: WorkIdRecord) => {
    onMerge?.(record);
  };

  const handleUnmerge = (record: WorkIdRecord) => {
    onUnmerge?.(record);
  };

  const handleReviewHistory = (record: WorkIdRecord) => {
    onReviewHistory?.(record);
  };

  const handleReopenTask = (record: WorkIdRecord) => {
    onReopenTask?.(record);
  };

  const handleReportAccomplishment = (record: WorkIdRecord) => {
    onReportAccomplishment?.(record);
  };

  const handleCloseTask = (record: WorkIdRecord) => {
    onCloseTask?.(record);
  };

  const handleUnassign = (record: WorkIdRecord) => {
    onUnassign?.(record);
  };

  const handleExportToPdf = async (record: WorkIdRecord) => {
    setExportError(null);
    try {
      setExportingId(record.id);
      const blob = await workIdService.exportToPdf(record.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = (record.workIdentificationId || `WorkID_${record.id}`).replace(/[/\\?*:<>|"\s]/g, '_');
      a.download = `WorkID_${label}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export to PDF failed:', err);
      const label = record.workIdentificationId ? ` for ${record.workIdentificationId}` : '';
      setExportError(describeActionError(err, `Couldn't export the PDF${label}. Try again.`));
    } finally {
      setExportingId(null);
    }
  };

  if (loading && workIds.length === 0) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center" style={{ minHeight: 0 }}>
        <Spinner role="status">Loading...</Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="tw-shrink-0">
        {error}
      </Alert>
    );
  }

  // One column config drives both the desktop table and the phone stacked-card layout
  // (DataTable). Work ID is the card title, Asset is the secondary identifier under it, and
  // Status + the actions kebab sit top-right as "trailing" — Merge and Source are icon/text
  // hints that are also reachable from the row menu, so they're dropped on mobile to keep the
  // card readable rather than crowding it with low-value fields.
  const columns: DataTableColumn<WorkIdRecord>[] = [
    {
      key: 'workIdentificationId',
      header: 'Work ID',
      sortable: true,
      mobile: 'title',
      // Pinned left while the other columns scroll sideways (DataTable `fill` pins the first column).
      className: 'tw-whitespace-nowrap',
      cell: (record) => (
        <>
          {onOpenDetail ? (
            <Button
              variant="link"
              className="tw-p-0 tw-align-baseline tw-no-underline"
              onClick={() => handleOpenDetail(record)}
              title="View details"
            >
              {record.workIdentificationId || '-'}
            </Button>
          ) : (
            record.workIdentificationId || '-'
          )}
          {record.createdFromFormId && (
            <>
              {' '}
              <Link
                to={`/submissions?formId=${record.createdFromFormId}`}
                className="tw-text-content-muted tw-text-sm"
                title="View form"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="bi bi-file-earmark-text" />
              </Link>
            </>
          )}
        </>
      ),
    },
    {
      key: 'merge',
      header: 'Merge',
      mobile: 'hidden',
      cell: (record) =>
        record.hasMergeSuggestions && record.status?.toUpperCase() !== 'MERGED' ? (
          <span title="Has merge suggestions">
            <i className="bi bi-diagram-3 tw-text-primary" aria-hidden />
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'reportedBy',
      header: 'Reported By',
      sortable: true,
      cell: (record) => formatReportedBy(record) || <EmptyValue />,
    },
    {
      key: 'reportedOn',
      header: 'Reported Date',
      sortable: true,
      cell: (record) => formatDate(record.reportedOn),
    },
    {
      key: 'completionDate',
      header: 'Completion Date',
      cell: (record) =>
        record.task?.completionDate ? formatDate(record.task.completionDate) : <EmptyValue />,
    },
    {
      key: 'asset',
      header: 'Asset',
      sortable: true,
      mobile: 'subtitle',
      // W-iP11: on iPad landscape the asset wrapped to 5 lines in a narrow column. A wider column
      // with a 2-line clamp (full text in the tooltip) keeps the row short.
      cell: (record) => {
        const asset = formatAsset(record);
        return asset === '-' ? (
          asset
        ) : (
          <div className="md:tw-min-w-[14rem] md:tw-max-w-[22rem]">
            <ClampText>{asset}</ClampText>
          </div>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      cell: (record) =>
        record.priority ? (
          <Badge bg={getPriorityVariant(record.priority)}>{formatPriority(record.priority)}</Badge>
        ) : (
          '-'
        ),
    },
    {
      key: 'foremanArea',
      header: 'Foreman Area',
      sortable: true,
      cell: (record) => record.foremanArea?.name || '-',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      cell: (record) => (
        <div className="tw-flex tw-items-center tw-gap-1">
          {record.status?.toUpperCase() === 'MERGED' &&
          onOpenMergedParent &&
          record.mergedIntoParent?.id != null ? (
            <button
              type="button"
              className="tw-border-0 tw-bg-transparent tw-p-0"
              onClick={() => onOpenMergedParent(record)}
              title={
                record.mergedIntoParent.workIdentificationId
                  ? `Open parent work ID ${record.mergedIntoParent.workIdentificationId}`
                  : `Open parent work ID #${record.mergedIntoParent.id}`
              }
              aria-label={
                record.mergedIntoParent.workIdentificationId
                  ? `Open parent work ID ${record.mergedIntoParent.workIdentificationId}`
                  : `Open parent work ID ${record.mergedIntoParent.id}`
              }
            >
              <StatusBadge status={record.status} label={formatWorkIdStatus(record.status)} vocabulary="workId" />
            </button>
          ) : (
            <StatusBadge status={record.status} label={formatWorkIdStatus(record.status)} vocabulary="workId" />
          )}
          {record.task?.status === 'ACTIVE_MARKED_AS_COMPLETE' && (
            <i
              className="bi bi-check-circle-fill tw-text-success"
              title="A linked task is Active – Marked as Complete. Completing the task will close the Work ID."
              aria-label="Completing the linked task will close the Work ID"
            />
          )}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      sortable: true,
      mobile: 'hidden',
      // W-iP11: Source was cut to one 180px line. Give it room and clamp to 2 lines instead.
      cell: (record) => {
        const source = formatSource(record);
        return source === '-' ? (
          <EmptyValue />
        ) : (
          <div className="md:tw-min-w-[12rem] md:tw-max-w-[20rem]">
            <ClampText>{source}</ClampText>
          </div>
        );
      },
    },
    {
      key: 'task',
      header: 'Task',
      cell: (record) =>
        record.task ? (
          <Link
            to={taskPageUrl(record.task, true)}
            className="tw-block tw-no-underline"
            title={`Task #${record.task.id} (${record.task.type}) – opens with option to return to Work IDs`}
          >
            {record.task.taskNumber ?? record.task.id} · {record.task.type}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      width: ACTIONS_COL_WIDTH,
      cell: (record) =>
        canEdit ||
        canApprove ||
        canCancel ||
        canCloseWithoutAction ||
        canAssign ||
        canMerge ||
        canReviewHistory ||
        canExport ||
        (record.task?.type === 'Routine' || record.task?.type === 'Quantified') ? (
          <WorkIdActions
            record={record}
            onEdit={handleEdit}
            onApprove={handleApprove}
            onCancel={handleCancel}
            onCloseWithoutAction={handleCloseWithoutAction}
            onReopen={handleReopen}
            onReopenTask={handleReopenTask}
            onAssignExisting={onAssignExisting}
            onCreateNewTask={onCreateNewTask}
            onUnassign={handleUnassign}
            onMerge={handleMerge}
            onViewMergeSuggestions={onViewMergeSuggestions}
            onUnmerge={handleUnmerge}
            onReviewHistory={handleReviewHistory}
            onExportToPdf={handleExportToPdf}
            onReportAccomplishment={handleReportAccomplishment}
            onCloseTask={handleCloseTask}
            exportingId={exportingId}
          />
        ) : (
          <span className="tw-text-content-muted">-</span>
        ),
    },
  ];

  return (
    // Fills ListPage.Body: the DataTable box takes the height and is the one scroll area; the
    // pagination keeps its height under it.
    <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col">
      {!hideFilters && (
        <div className="tw-shrink-0">
          <WorkIdFilters
            filterOptions={filterOptions}
            filters={filters}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleApplyFilters}
          />
        </div>
      )}

      {exportError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setExportError(null)} className="tw-mb-2 tw-shrink-0">
          {exportError}
        </Alert>
      )}

      {/* No wrapper box around the table: DataTable `fill` makes its own box the scroller, so
          the sticky header attaches to it, Work ID and Actions are pinned, and the horizontal
          scrollbar is at the bottom (an extra overflow wrapper here used to catch the sticky). */}
      <DataTable
          columns={columns}
          data={workIds}
          rowKey={(record) => record.id}
          striped
          hover
          fill
          loading={loading}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          emptyMessage="No work IDs found."
        />

      {pagination.totalCount > 0 && (
        <div className="tw-shrink-0">
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            className="tw-mt-2"
          />
        </div>
      )}
    </div>
  );
};

export default WorkIdsTable;
